import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'utils/classnames';
import { DISPOSITION_COLORS } from 'utils/constants';
import { translateProblemMessage } from 'utils/problemMessages';

interface ProblemResult {
  responseMsg?: string | null;
  tracking_id?: string | null;
}

interface Props {
  result: ProblemResult;
  scanInput?: ReactNode;
  children?: ReactNode;
}

export const ProblemScreen = ({ result, scanInput, children }: Props) => {
  const { t } = useTranslation();
  const colors = DISPOSITION_COLORS.PROBLEM;
  const subheading = translateProblemMessage(result.responseMsg);

  return (
    <div
      className={cn(
        '-mx-4 -mb-6 flex min-h-full flex-col items-center px-6 pb-6 pt-4',
        colors.gradient
      )}
    >
      {scanInput && <div className="mb-6 w-full">{scanInput}</div>}

      <div className="mb-4 text-center">
        <p className="text-6xl font-black uppercase tracking-widest text-white leading-none">
          {t('problemScreen.title')}
        </p>
        <p className="text-3xl my-3 font-black uppercase tracking-widest text-white">
          {subheading ?? t('problemScreen.defaultMessage')}
        </p>
      </div>

      <h1 className="mb-6 text-base font-bold text-white">
        {t('problemScreen.trackingPrefix')} {result.tracking_id}
      </h1>

      {children && <div className="w-full flex flex-col gap-3 mb-6">{children}</div>}

      <div className="flex flex-col items-center gap-6 text-center">
        <div className="animate-pulse">
          <div
            className={cn(
              'flex h-28 w-28 items-center justify-center rounded-full bg-white/20 ring-4 ring-white/40 ring-offset-2',
              colors.ringOffset
            )}
          >
            <span className="text-6xl">{colors.icon}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
